/**
 * PATH: packages/auth/src/identityScope.ts
 * FILE_ID: AUTH-IDENTITY-SCOPE-001
 * PURPOSE: Identity-scoped React Query key helper (userId + orgId prefix).
 * WHY:
 *   Without identity in the queryKey, React Query's cache is shared across
 *   identities within the same tab/page lifetime. That is the root failure
 *   mode behind BUG-2026-05-21-CROSS-TAB-IDENTITY-DRIFT — a refresh-token
 *   swap, an in-tab re-login, or a logout-without-reload can leave the
 *   cache populated with the previous identity's data, then surface it to
 *   the new identity.
 *
 *   Option 3 of the cross-tab fix (BUG-2026-05-21-CROSS-TAB-IDENTITY-DRIFT)
 *   wipes the cache on identity transitions detected at the BOUNDARY
 *   (server header / sessionStorage pin). This module is the defence-in-
 *   depth layer: every queryKey is silently prefixed with [userId, orgId],
 *   so even if the boundary check misses a transition, the new identity
 *   gets a cache miss instead of stale data.
 *
 * FLOW:
 *   ┌──────────────────┐    ┌──────────────────┐    ┌──────────────────┐
 *   │  identity source │ →  │ withIdentityKey  │ →  │  React Query key │
 *   │  (auth store /   │    │  (pure prefix)   │    │  [u,o,...base]   │
 *   │   tab pin)       │    │                  │    │                  │
 *   └──────────────────┘    └──────────────────┘    └──────────────────┘
 *
 * DEPENDENCIES:
 *   - react (peer)   — useSyncExternalStore for the hook
 *   - ./authStore    — useAuthStore for the hook
 *   - ./cookieAuth   — getOrgCookie for the sync fallback
 *
 * RELATED:
 *   - packages/auth/src/session-manager/tabIdentity.ts — server-asserted
 *     identity pin in sessionStorage (the same identity used here).
 *   - docs/bugs/BUG_LEDGER.md (BUG-2026-05-21-CROSS-TAB-IDENTITY-DRIFT)
 */

import { useMemo } from 'react'
import { useAuthStore } from './authStore'
import { getOrgCookie } from './cookieAuth'
import { TAB_IDENTITY_KEY } from './session-manager/tabIdentity'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * The identity dimensions that gate a query's cache.
 * - `userId` — the authenticated subject. We use the user.id from the
 *   auth store (numeric or string). Falls back to email for legacy users
 *   that have not been mapped to a stable id.
 * - `orgId`  — the organisation context for multi-tenant queries.
 */
export interface IdentityScope {
  userId: string | null
  orgId: string | null
}

/**
 * A queryKey base — the domain-specific tuple the caller would pass to
 * `useQuery({ queryKey: ... })` if there were no identity scoping. We
 * accept anything serialisable React Query accepts.
 */
export type QueryKeyBase = readonly unknown[]

/**
 * The output of `withIdentityKey` — a queryKey with two identity slots
 * prepended. The two slots are ALWAYS string-or-null so React Query's
 * structural equality is stable across renders (no `0` vs `'0'` drift).
 */
export type IdentityScopedQueryKey = readonly [
  /* userIdSlot */ string | null,
  /* orgIdSlot */ string | null,
  ...QueryKeyBase,
]

// ---------------------------------------------------------------------------
// Pure helpers (no React, no hooks — safe to import from anywhere)
// ---------------------------------------------------------------------------

/**
 * Normalise an id-like value to a stable string (or null). React Query
 * keys are compared structurally — `1` and `'1'` are different cache
 * entries — so we coerce both to `'1'` for consistency. We also lower-
 * case so email-based ids ("Foo@bar.com" vs "foo@bar.com") collapse.
 */
function _coerceId(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) return null
  const stringified = typeof value === 'number' ? String(value) : value
  const trimmed = stringified.trim()
  if (!trimmed) return null
  return trimmed.toLowerCase()
}

/**
 * Pure prefixer. The single source of truth for the [userId, orgId, ...]
 * layout that React Query keys use. Centralising this means a future
 * change (e.g. adding `tabId` for multi-account scenarios) is one
 * line in this file rather than 952 call sites.
 *
 * @example
 *   withIdentityKey({ userId: 5, orgId: 12 }, ['jobs', 'list', filters])
 *   // → ['5', '12', 'jobs', 'list', filters]
 */
export function withIdentityKey<T extends QueryKeyBase>(
  identity: { userId: string | number | null | undefined; orgId: string | number | null | undefined },
  base: T,
): IdentityScopedQueryKey {
  return [_coerceId(identity.userId), _coerceId(identity.orgId), ...base] as const
}

/**
 * Synchronous identity read for code paths that don't have access to a
 * React hook (e.g. axios interceptors, queryFn bodies in non-React
 * modules, the email-hub's progressive fetcher).
 *
 * Source priority:
 *   1. `sessionStorage['quotech_tab_user_id']` — the per-tab pin set by
 *      `pinTabIdentity` at boot. This is the same value the server
 *      stamps in X-User-Id, so reading it here keeps the cache key in
 *      lockstep with the server-asserted identity (Option 3).
 *   2. `useAuthStore.getState().user?.id` — fallback when the pin isn't
 *      present yet (very early boot before `pinTabIdentity` runs).
 *
 * The `orgId` reads from the auth store first (most authoritative for
 * the current React tree) and falls back to the org cookie.
 */
export function getIdentityForKey(): IdentityScope {
  let userId: string | null = null
  let orgId: string | null = null

  if (typeof sessionStorage !== 'undefined') {
    try {
      userId = _coerceId(sessionStorage.getItem(TAB_IDENTITY_KEY))
    } catch {
      // sessionStorage can throw in private browsing / sandboxed iframes
      userId = null
    }
  }

  if (!userId) {
    try {
      const state = useAuthStore.getState()
      userId = _coerceId(state.user?.id ?? state.user?.email ?? null)
    } catch {
      userId = null
    }
  }

  try {
    const state = useAuthStore.getState()
    orgId = _coerceId(state.organisationId)
  } catch {
    orgId = null
  }

  if (!orgId) {
    try {
      const cookie = getOrgCookie()
      orgId = _coerceId(cookie?.id ?? null)
    } catch {
      orgId = null
    }
  }

  return { userId, orgId }
}

// ---------------------------------------------------------------------------
// React hook
// ---------------------------------------------------------------------------

/**
 * Hook variant — reads identity from the auth store with React-aware
 * subscription so the query refetches when the identity changes mid-
 * render (e.g. an SSO redirect lands and `setAuth` fires).
 *
 * For most call sites, you want `useIdentityScopedKey(base)` instead —
 * it returns a queryKey directly. Use `useIdentityScope()` only when
 * you need the raw `{ userId, orgId }` values (e.g. to construct a key
 * inside a `useMemo` body alongside other dependencies).
 */
export function useIdentityScope(): IdentityScope {
  const userId = useAuthStore((s) => _coerceId(s.user?.id ?? s.user?.email ?? null))
  const orgId = useAuthStore((s) => _coerceId(s.organisationId))
  return { userId, orgId }
}

/**
 * Hook that returns a queryKey with identity prefixed. The returned
 * key is memoised on the identity AND the base contents so React Query
 * sees stable references across renders that don't actually change the
 * key shape.
 *
 * @example
 *   const { data } = useQuery({
 *     queryKey: useIdentityScopedKey(['jobs', 'list', filters]),
 *     queryFn: () => fetchJobs(filters),
 *   })
 */
export function useIdentityScopedKey<T extends QueryKeyBase>(
  base: T,
): IdentityScopedQueryKey {
  const { userId, orgId } = useIdentityScope()
  // Stringify the base for stable memo dependency tracking. React Query
  // does its own structural diffing, but we additionally want to avoid
  // returning a fresh array on every render when the contents haven't
  // changed — that's what keeps `enabled` toggles + `useMemo` consumers
  // downstream from over-firing.
  const baseSignature = useMemo(() => {
    try {
      return JSON.stringify(base)
    } catch {
      // Non-serialisable members (e.g. Date / RegExp) fall through to
      // identity-equality on the array itself. This is the legacy
      // behaviour — the caller is responsible for stable references.
      return base
    }
  }, [base])
  return useMemo(
    () => withIdentityKey({ userId, orgId }, base),
    // baseSignature is the canonical signal; including `base` directly
    // would defeat the memoisation for fresh-array-same-contents callers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, orgId, baseSignature],
  )
}

// ---------------------------------------------------------------------------
// QueryClient integration — auto-prefix EVERY queryKey hash with the
// current identity. This is the wholesale defence layer that
// retroactively partitions every `useQuery`, `setQueryData`,
// `getQueryData`, and `invalidateQueries({ exact: true })` call by
// [userId, orgId] without touching any call site.
// ---------------------------------------------------------------------------

/**
 * Stable hash for a queryKey. Mirrors the default `hashKey` from
 * `@tanstack/react-query` — JSON-stringify with sorted object keys
 * so different argument orderings hash to the same string. Inlined
 * (rather than imported from `@tanstack/query-core`) so this module
 * has no runtime dependency on RQ; consumers that don't use RQ
 * don't pay the dep cost.
 */
function _stableStringify(value: unknown): string {
  if (value === null || value === undefined) return JSON.stringify(value ?? null)
  if (typeof value !== 'object') return JSON.stringify(value)
  if (Array.isArray(value)) {
    return `[${(value as unknown[]).map(_stableStringify).join(',')}]`
  }
  const entries = Object.keys(value as Record<string, unknown>)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${_stableStringify((value as Record<string, unknown>)[k])}`)
  return `{${entries.join(',')}}`
}

/**
 * Build the queryKeyHashFn to plug into a QueryClient's
 * `defaultOptions.queries.queryKeyHashFn`. Every queryKey passed
 * through React Query's cache machinery will hash to a string that
 * includes the current identity, so:
 *
 *   - `useQuery({ queryKey: ['jobs'] })` for user 5 lands in cache
 *     slot `[5, org-3, "jobs"]`.
 *   - The same `useQuery({ queryKey: ['jobs'] })` for user 6 lands
 *     in cache slot `[6, org-7, "jobs"]` — different slot, cache
 *     miss, fetches fresh.
 *
 * Identity is read at HASH TIME via `getIdentityForKey()` — the
 * sessionStorage tab pin first (Option 3 of the cross-tab fix),
 * then the auth store. The pin is the same value the server
 * stamps in `X-User-Id`, so the cache key stays in lockstep with
 * the server-asserted identity.
 *
 * The original queryKey array on the Query object is unchanged —
 * only the HASH (cache-slot lookup) is identity-aware. So
 * partial-match invalidations like `invalidateQueries({ queryKey:
 * ['jobs'] })` still work as before.
 *
 * @example
 *   // packages/api/src/provider.tsx
 *   const queryClient = new QueryClient({
 *     defaultOptions: {
 *       queries: {
 *         queryKeyHashFn: makeIdentityScopedQueryKeyHashFn(),
 *         ...
 *       },
 *     },
 *   })
 */
export function makeIdentityScopedQueryKeyHashFn(): (queryKey: readonly unknown[]) => string {
  return function identityScopedHash(queryKey: readonly unknown[]): string {
    const { userId, orgId } = getIdentityForKey()
    return _stableStringify([userId, orgId, ...queryKey])
  }
}

// ---------------------------------------------------------------------------
// Test surface (do not call from production code — exported for
// unit/source-pin tests only).
// ---------------------------------------------------------------------------

/** @internal — exposed for tests. */
export const __identityScopeInternals = {
  coerceId: _coerceId,
  stableStringify: _stableStringify,
}
